import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";

/**
 * Holds deposit config count used for onboarding nav visibility.
 * Dashboard updates this when it fetches (on mount); visibility hook reads it
 * and only fetches when value is null (so we don't refetch on every URL change).
 */
type DepositConfigCountSetter =
  | number
  | null
  | ((prev: number | null) => number | null);

interface OnboardingContextType {
  depositConfigCount: number | null;
  setDepositConfigCount: (count: DepositConfigCountSetter) => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [depositConfigCount, setDepositConfigCount] = useState<number | null>(
    null,
  );
  const setCount = useCallback((count: DepositConfigCountSetter) => {
    setDepositConfigCount(count);
  }, []);

  // [LOOP] churn probe
  useValueChurnProbe("OnboardingContext", { depositConfigCount });

  // Memoized (loop fix): the old inline literal minted a new identity every
  // render, re-rendering consumers (NavBar's visibility hook) needlessly.
  const value = useMemo(
    () => ({ depositConfigCount, setDepositConfigCount: setCount }),
    [depositConfigCount, setCount],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}
